/**
 * Post-SMS Terms gate — portal / native parity.
 * Accept stamps users/{uid}.termsAcceptedAt; decline drops an unconsented signup.
 */
import { useState } from 'react';
import FloatingFooter, {
  FloatingFooterCta,
  FloatingFooterNavButton,
} from './FloatingFooter';
import TermsConsent from './TermsConsent';
import { getFirebaseAuth } from '../config/firebase';
import {
  acceptTermsAndCreateProfile,
  declineTermsAndResetSession,
} from '../services/userProfile';
import { detectAppLocale } from '../constants/legalUrls';

const COPY = {
  en: {
    title: 'Almost there',
    body: 'To create your account, accept the terms. If you don’t accept, nothing will be saved.',
    accept: 'Accept and continue',
    accepting: 'Creating account…',
    decline: "I don’t accept",
    needCheck: 'Please agree to the Terms of Service and Privacy Policy.',
    acceptFailed: 'Couldn’t create your account. Please try again.',
    declineFailed: 'Couldn’t cancel. Please try again.',
  },
  pt: {
    title: 'Quase lá',
    body: 'Para criar sua conta, aceite os termos. Se não aceitar, nada será registrado.',
    accept: 'Aceitar e continuar',
    accepting: 'Criando conta…',
    decline: 'Não aceito',
    needCheck: 'Aceite os Termos de Serviço e a Política de Privacidade.',
    acceptFailed: 'Não foi possível criar a conta. Tente novamente.',
    declineFailed: 'Não foi possível cancelar. Tente novamente.',
  },
} as const;

type Props = {
  onAccepted: () => void;
  onDeclined: () => void;
  eyebrow?: string;
};

export default function TermsGate({ onAccepted, onDeclined, eyebrow = 'Account' }: Props) {
  const copy = detectAppLocale() === 'pt' ? COPY.pt : COPY.en;
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accept = async () => {
    if (!checked) {
      setError(copy.needCheck);
      return;
    }
    const user = getFirebaseAuth().currentUser;
    if (!user) {
      setError(copy.acceptFailed);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await acceptTermsAndCreateProfile(user);
      onAccepted();
    } catch {
      setError(copy.acceptFailed);
      setBusy(false);
    }
  };

  const decline = async () => {
    setError(null);
    setBusy(true);
    try {
      await declineTermsAndResetSession();
      onDeclined();
    } catch {
      setError(copy.declineFailed);
      setBusy(false);
    }
  };

  return (
    <div className="wizard-shell">
      <header className="page-header !pb-1">
        <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-x9-marineSoft">
          {eyebrow}
        </span>
      </header>

      <div className="page-body">
        <h1 className="text-[28px] font-bold tracking-tight text-x9-ink">
          {copy.title}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-x9-marineSoft">
          {copy.body}
        </p>

        <div className="mt-6">
          <TermsConsent
            checked={checked}
            onChange={setChecked}
            disabled={busy}
          />
        </div>

        {error && (
          <p className="mt-4 text-sm text-rose-600">{error}</p>
        )}
      </div>

      <FloatingFooter>
        <FloatingFooterNavButton
          label={copy.decline}
          onClick={() => void decline()}
          disabled={busy}
        />
        <FloatingFooterCta
          disabled={busy || !checked}
          aria-busy={busy}
          onClick={() => void accept()}
        >
          {busy ? copy.accepting : copy.accept}
        </FloatingFooterCta>
      </FloatingFooter>
    </div>
  );
}
